// Outbound calling utility functions and config caching

const OUTBOUND_CONFIG_KEY = 'outbound_call_config';
const CONFIG_EXPIRY_KEY = 'outbound_call_config_expiry';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export interface OutboundCallConfig {
  uuid: string;
  provider: 'knowlarity' | 'tata_tele' | 'mcube';
  provider_name: string;
  is_active: boolean;
}

/**
 * Save outbound config to localStorage with expiry
 */
export function cacheOutboundConfig(config: OutboundCallConfig | null): void {
  if (config) {
    localStorage.setItem(OUTBOUND_CONFIG_KEY, JSON.stringify(config));
    localStorage.setItem(CONFIG_EXPIRY_KEY, String(Date.now() + CACHE_DURATION));
  } else {
    localStorage.removeItem(OUTBOUND_CONFIG_KEY);
    localStorage.removeItem(CONFIG_EXPIRY_KEY);
  }
}

/**
 * Get cached outbound config from localStorage
 * Returns null if expired or not found
 */
export function getCachedOutboundConfig(): OutboundCallConfig | null {
  try {
    const expiryStr = localStorage.getItem(CONFIG_EXPIRY_KEY);
    const configStr = localStorage.getItem(OUTBOUND_CONFIG_KEY);

    if (!expiryStr || !configStr) {
      return null;
    }

    const expiry = parseInt(expiryStr, 10);
    if (Date.now() > expiry) {
      // Cache expired, clear it
      clearOutboundConfigCache();
      return null;
    }

    return JSON.parse(configStr);
  } catch (error) {
    console.error('Error reading cached config:', error);
    return null;
  }
}

/**
 * Clear cached outbound config
 */
export function clearOutboundConfigCache(): void {
  localStorage.removeItem(OUTBOUND_CONFIG_KEY);
  localStorage.removeItem(CONFIG_EXPIRY_KEY);
}

/**
 * Check if outbound calling is configured and active
 */
export function isOutboundCallingAvailable(): boolean {
  const config = getCachedOutboundConfig();
  return config !== null && config.is_active;
}
